/**
 * Small localStorage record of "where I was poking around", separate from
 * the real save (WorldRepository/SaveSystem).
 *
 * Free flight deliberately never touches save-worthy state — it detaches a
 * camera, not the vessel (see FreeFlight.ts) — which is exactly right for
 * not letting debug exploration corrupt a save, but it means a page refresh
 * always snapped the camera back to the vessel. That refresh is routine here:
 * a shader edit needs a full reload, not HMR (see CLAUDE.md), so anyone
 * iterating on rendering with free flight loses their vantage point on every
 * single check. This is the same load/save-with-safe-fallback shape as
 * GraphicsSettings.ts, kept as its own key since it is not a graphics
 * preference and should not round-trip through that file's shape.
 */

const STORAGE_KEY = 'sfx.devSession.v1';

export interface DevSessionFreeFlight {
  bodyId: string;
  frame: 'inertial' | 'fixed';
  position: [number, number, number];
  orientation: [number, number, number, number];
  speedExponent: number;
}

export interface DevSessionState {
  /** Which launcher panel (Map, Debug, ...) was open, or null. */
  panel: string | null;
  /** Null when free flight was off at last write. */
  freeFlight: DevSessionFreeFlight | null;
  /**
   * Which HUD groups were on screen, keyed by `HudSectionId`. Null means
   * "never chosen", which is what lets a first launch pick a default from the
   * viewport size instead of overriding the player on every device they open
   * the game on. Stored loosely typed and validated on read by
   * `sanitizeHudVisibility`, since the section list can change between builds.
   */
  hud: Record<string, boolean> | null;
}

const DEFAULTS: DevSessionState = { panel: null, freeFlight: null, hud: null };

function isFreeFlight(v: unknown): v is DevSessionFreeFlight {
  if (!v || typeof v !== 'object') return false;
  const f = v as Record<string, unknown>;
  return (
    typeof f.bodyId === 'string' &&
    (f.frame === 'inertial' || f.frame === 'fixed') &&
    Array.isArray(f.position) &&
    f.position.length === 3 &&
    Array.isArray(f.orientation) &&
    f.orientation.length === 4 &&
    typeof f.speedExponent === 'number'
  );
}

/**
 * Reads the saved session. Storage can be unavailable (private browsing,
 * disabled by policy) or hold a malformed or older blob; either way, falling
 * back to "nothing saved" is preferable to failing to start.
 */
export function loadDevSessionState(): DevSessionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<DevSessionState>;
    return {
      panel: typeof parsed.panel === 'string' ? parsed.panel : DEFAULTS.panel,
      freeFlight: isFreeFlight(parsed.freeFlight) ? parsed.freeFlight : DEFAULTS.freeFlight,
      hud: parsed.hud && typeof parsed.hud === 'object' ? (parsed.hud as Record<string, boolean>) : DEFAULTS.hud,
    };
  } catch (err) {
    console.warn('[DevSessionState] failed to read saved session', err);
    return { ...DEFAULTS };
  }
}

export function saveDevSessionState(state: DevSessionState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.warn('[DevSessionState] failed to save session', err);
  }
}

/** Read-modify-write helper so callers only touch the field they own. */
export function patchDevSessionState(patch: Partial<DevSessionState>): void {
  saveDevSessionState({ ...loadDevSessionState(), ...patch });
}
